import styled from '@emotion/styled';
import { BaseButton } from '../base/button/button';
import { Variables } from '../base/variables/variables';

const V = Variables.ThemeLight;

export const Body = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    background: ${V.Background3};
`;

export const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px 20px 12px;
    border-bottom: 1px solid ${V.Border1};
`;

export const TitleRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
`;

export const Kicker = styled.div`
    color: ${V.TextMild};
    font-size: 11px;
`;

export const Title = styled.div`
    color: ${V.TextStrong};
    font-size: 16px;
    font-weight: ${Variables.FontWeight.semibold};
    margin-top: 2px;
`;

export const Meta = styled.div`
    color: ${V.TextMedium};
    font-size: 12px;
    margin-top: 4px;
`;

export const Muted = styled.div`
    color: ${V.TextMild};
    font-size: 12px;
`;

export const Toolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

export const Pager = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    color: ${V.TextMild};
    font-size: 12px;
    font-variant-numeric: tabular-nums;
`;

export const ColumnHead = styled.div`
    display: grid;
    grid-template-columns: 20px minmax(0, 1.6fr) minmax(0, 1.2fr) minmax(0, 0.8fr);
    gap: 8px;
    padding: 8px 20px;
    border-bottom: 1px solid ${V.Border1};
    color: ${V.TextMild};
    font-size: 11px;
    font-weight: ${Variables.FontWeight.semibold};
    letter-spacing: 0.04em;
    text-transform: uppercase;

    & > :last-of-type {
        text-align: right;
    }
`;

export const Scroll = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
`;

export const Row = styled(BaseButton)<{ open: boolean }>`
    display: grid;
    grid-template-columns: 20px minmax(0, 1.6fr) minmax(0, 1.2fr) minmax(0, 0.8fr);
    gap: 8px;
    width: 100%;
    padding: 10px 20px;
    border-bottom: 1px solid ${V.Border1};
    background: ${({ open }) => (open ? V.Background1 : 'transparent')};
    text-align: left;
    color: ${V.TextMedium};
    font-size: 12px;

    &:hover {
        background: ${V.Background1};
    }
`;

export const Chevron = styled.span`
    color: ${V.TextMild};
    padding-top: 1px;
`;

export const Stack = styled.span<{ align?: 'right' }>`
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    text-align: ${({ align }) => align || 'left'};
`;

export const ClaimId = styled.span`
    color: ${V.PrimaryConstant6};
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11.5px;
    word-break: break-all;
`;

export const Strong = styled.span`
    color: ${V.TextStrong};
`;

export const Ellipsis = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const Expanded = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 12px 20px 16px 48px;
    border-bottom: 1px solid ${V.Border1};
    background: ${V.Background2};
`;

/**
 * One field per row: core's `DetailField` is a label-beside-value row with a fixed label column, so it
 * reads as a list here rather than the mockup's four-column grid.
 */
export const Fields = styled.div`
    display: flex;
    flex-direction: column;
`;

export const Money = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    border: 1px solid ${V.Border1};
    border-radius: ${Variables.Rounded.medium};
    background: ${V.Background3};

    & > * {
        padding: 8px 10px;
    }

    & > * + * {
        border-left: 1px solid ${V.Border1};
    }
`;

export const MoneyLabel = styled.div`
    color: ${V.TextMild};
    font-size: 11px;
`;

export const MoneyValue = styled.div`
    color: ${V.TextStrong};
    font-size: 14px;
    font-weight: ${Variables.FontWeight.semibold};
    font-variant-numeric: tabular-nums;
    margin-top: 2px;
`;

export const Actions = styled.div`
    display: flex;
    gap: 8px;
`;

export const Center = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 32px 20px;
    color: ${V.TextMild};
    font-size: 12px;
    text-align: center;
`;
